/**
 * HubSpot form instrumentation.
 *
 * Every listener here is delegated on `document`, never bound to the form.
 * That is not a style preference: the theme's form block wipes its container
 * (`container.innerHTML = ''`) on `swup:content:replace` and rebuilds the
 * form inside a double requestAnimationFrame, so any listener attached to the
 * form node dies on the first SPA navigation. Delegation survives every swap
 * and cannot double-bind.
 *
 * The form GUID is read from the markup the theme already prints —
 * `[data-form-guid]` on the form block, the post lead gate and the expert
 * modals — so nothing about the theme has to change for this to work.
 *
 * Field NAMES and interaction events only. No field value is ever read,
 * stored or sent.
 */

import type { FieldMetric } from '../types.ts';

export interface FieldEvent {
	name: string;
	metric: FieldMetric;
	ms?: number;
}

export interface FormCallbacks {
	/** The form for this container became visible. */
	onView: (guid: string, container: Element) => void;
	/** First real interaction with any field. */
	onStart: (guid: string, container: Element) => void;
	onField: (guid: string, container: Element, event: FieldEvent) => void;
	onSubmit: (guid: string, container: Element | null) => void;
}

const CONTAINER_SELECTOR = '[data-form-guid]';

/** Fields we never report: hidden inputs, buttons, HubSpot's own plumbing. */
const isTrackable = (el: Element): el is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement => {
	if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement)) {
		return false;
	}
	if (el instanceof HTMLInputElement && ['hidden', 'submit', 'button', 'reset', 'image'].includes(el.type)) {
		return false;
	}
	return fieldName(el) !== '';
};

const fieldName = (el: Element): string => {
	const named = el.getAttribute('name');
	if (named && named.trim() !== '') return named.replace(/\s+/g, '').slice(0, 190);

	// Some HubSpot controls carry no name at all (the phone country select on
	// /book-a-demo/, for one) and can only be identified by id — but that id
	// embeds the form GUID twice over, e.g.
	// `phone_ext-a3fb55dd-…_hubspot-form-a3fb55dd-…1-3`, which is unreadable in
	// a report. Cut it at the GUID and keep the human part.
	const id = el.getAttribute('id') ?? '';
	const trimmed = id.replace(/-[0-9a-f]{8}-[0-9a-f]{4}-.*$/i, '');
	return (trimmed || id).replace(/\s+/g, '').slice(0, 190);
};

const isFilled = (el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): boolean => {
	if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) return el.checked;
	// Only ever asks WHETHER something was entered, never what.
	return el.value.trim() !== '';
};

const guidOf = (container: Element): string => (container.getAttribute('data-form-guid') ?? '').toLowerCase();

interface FormState {
	started: boolean;
	submitted: boolean;
	viewed: boolean;
	/** A <form> has appeared inside this container at least once. */
	formSeen: boolean;
	/** Field currently focused, and when focus landed. */
	focus: { name: string; at: number; el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement } | null;
	/** Last field focused that was still empty when focus left. */
	lastEmpty: string | null;
	/** Abandonment already reported, so two exit signals count once. */
	abandoned: boolean;
	/**
	 * A HubSpot lifecycle callback has been seen for this container, so
	 * HubSpot — not the native submit event — is the authority on success.
	 */
	hsManaged: boolean;
}

const states = new WeakMap<Element, FormState>();

const stateFor = (container: Element): FormState => {
	let state = states.get(container);
	if (!state) {
		state = {
			started: false,
			submitted: false,
			viewed: false,
			formSeen: false,
			focus: null,
			lastEmpty: null,
			abandoned: false,
			hsManaged: false,
		};
		states.set(container, state);
	}
	return state;
};

export const init = (callbacks: FormCallbacks, trackFields: boolean): void => {
	// ----- visibility -----
	const seen = new WeakSet<Element>();
	const observer = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (!entry.isIntersecting) continue;
				const container = entry.target;
				const state = stateFor(container);
				if (state.viewed) continue;
				// Only count a view once the form actually rendered. An empty
				// container scrolling past is not a form the visitor saw.
				if (!container.querySelector('form')) continue;
				state.viewed = true;
				callbacks.onView(guidOf(container), container);
			}
		},
		{ threshold: 0.3 },
	);

	const observeContainers = (): void => {
		for (const container of document.querySelectorAll(CONTAINER_SELECTOR)) {
			const state = stateFor(container);

			// The container is in the DOM long before HubSpot injects the form
			// into it. If it was already on screen at that point, the
			// intersection callback has come and gone with no <form> to see,
			// and IntersectionObserver will not fire again without a CHANGE in
			// visibility — so a visitor sitting on the form would never be
			// counted as having seen it. Re-observing on the moment the form
			// appears re-delivers the current intersection state, which is the
			// only way to catch that case.
			if (!state.formSeen && container.querySelector('form')) {
				state.formSeen = true;
				if (seen.has(container)) {
					observer.unobserve(container);
					observer.observe(container);
					continue;
				}
			}

			if (seen.has(container)) continue;
			seen.add(container);
			observer.observe(container);
		}
	};

	// HubSpot injects the form long after DOMContentLoaded, and Swup rebuilds
	// the container, so containers are re-checked as the DOM changes rather
	// than once.
	observeContainers();
	const mutations = new MutationObserver(() => observeContainers());
	mutations.observe(document.documentElement, { childList: true, subtree: true });
	document.addEventListener('swup:content:replace', () => {
		// The theme's form block empties its container on this event, so any
		// form we had noticed is gone. Reset the appearance flag or the
		// rebuilt form is never re-evaluated for visibility.
		for (const container of document.querySelectorAll(CONTAINER_SELECTOR)) {
			const state = states.get(container);
			if (state) state.formSeen = false;
		}
		window.setTimeout(observeContainers, 0);
	});

	if (!trackFields) {
		wireSubmit(callbacks);
		return;
	}

	// ----- field interaction -----
	document.addEventListener(
		'focusin',
		(event) => {
			const el = event.target;
			if (!(el instanceof Element) || !isTrackable(el)) return;
			const container = el.closest(CONTAINER_SELECTOR);
			if (!container) return;
			const state = stateFor(container);
			const name = fieldName(el);

			if (!state.started) {
				state.started = true;
				callbacks.onStart(guidOf(container), container);
			}
			state.focus = { name, at: Date.now(), el };
			callbacks.onField(guidOf(container), container, { name, metric: 'reached' });
		},
		true,
	);

	document.addEventListener(
		'focusout',
		(event) => {
			const el = event.target;
			if (!(el instanceof Element) || !isTrackable(el)) return;
			const container = el.closest(CONTAINER_SELECTOR);
			if (!container) return;
			const state = stateFor(container);
			const name = fieldName(el);
			const dwell = state.focus && state.focus.name === name ? Date.now() - state.focus.at : 0;
			state.focus = null;

			if (isFilled(el)) {
				state.lastEmpty = null;
				// The dwell rides on `filled`, so the reported average is
				// time-to-fill rather than time-spent-anywhere-near-the-field.
				callbacks.onField(guidOf(container), container, { name, metric: 'filled', ms: dwell });
				return;
			}
			// Left empty. `reached` was already reported on focus, and
			// re-reporting it here is what used to turn one visitor into
			// several. Just remember the field as the abandonment candidate,
			// resolved at pagehide or on a Swup exit if no submit happens.
			state.lastEmpty = name;
		},
		true,
	);

	document.addEventListener(
		'invalid',
		(event) => {
			const el = event.target;
			if (!(el instanceof Element) || !isTrackable(el)) return;
			const container = el.closest(CONTAINER_SELECTOR);
			if (!container) return;
			callbacks.onField(guidOf(container), container, { name: fieldName(el), metric: 'error' });
		},
		true,
	);

	wireSubmit(callbacks);

	// ----- abandonment -----
	const sweepAbandonment = (): void => {
		for (const container of document.querySelectorAll(CONTAINER_SELECTOR)) {
			const state = states.get(container);
			if (!state || !state.started || state.submitted || state.abandoned) continue;
			const name = state.focus?.name ?? state.lastEmpty;
			if (!name) continue;
			state.abandoned = true;
			callbacks.onField(guidOf(container), container, { name, metric: 'abandoned_here' });
		}
	};

	// pagehide rather than unload: it fires for bfcache navigations too, and
	// unload is unreliable on mobile Safari.
	window.addEventListener('pagehide', sweepAbandonment);

	// A Swup navigation never unloads the document, so pagehide does NOT fire
	// when a visitor abandons the form by clicking any internal link — which
	// is most of them. visit:start is the moment they decided to leave, and it
	// runs before the block wipes the container.
	document.addEventListener('swup:visit:start', sweepAbandonment);
};

const wireSubmit = (callbacks: FormCallbacks): void => {
	const count = (container: Element): void => {
		const state = stateFor(container);
		if (state.submitted) return;
		state.submitted = true;
		callbacks.onSubmit(guidOf(container), container);
	};

	// The native submit event is NOT proof of a submission.
	//
	// HubSpot's form fires it and then rejects the payload in its own
	// validation — measured on /book-a-demo/, where the required privacy
	// consent box was unticked: the submit event fired, HubSpot showed
	// "Please complete all required fields", and nothing was sent. Counting
	// that would have recorded a conversion that never happened.
	//
	// So for any form HubSpot manages (proved by ANY hsFormCallback, which
	// includes onFormReady at load), onFormSubmitted below is the authority
	// and this listener does nothing. It stays for plain non-HubSpot forms,
	// which have no callback channel to defer to.
	document.addEventListener(
		'submit',
		(event) => {
			const form = event.target;
			if (!(form instanceof HTMLFormElement)) return;
			const container = form.closest(CONTAINER_SELECTOR);
			if (!container) return;
			if (stateFor(container).hsManaged) return;
			count(container);
		},
		true,
	);

	// HubSpot's own lifecycle channel. Both the legacy and the new embed post
	// these, and the theme already relies on them.
	window.addEventListener('message', (event: MessageEvent) => {
		const data = event.data as { type?: string; eventName?: string; id?: string } | null;
		if (!data || data.type !== 'hsFormCallback') return;

		const guid = (data.id ?? '').toLowerCase();
		let container: Element | null = guid ? document.querySelector(`[data-form-guid="${CSS.escape(guid)}"]`) : null;
		// A callback that reports no id: fall back to the only form on the
		// page, and stay silent when that is ambiguous rather than guessing.
		if (!container) {
			const all = document.querySelectorAll(CONTAINER_SELECTOR);
			container = all.length === 1 ? all[0]! : null;
		}
		if (!container) return;

		// Any callback at all means HubSpot owns this form's outcome.
		stateFor(container).hsManaged = true;

		// Count on EITHER lifecycle event, whichever arrives first.
		//
		// `onFormSubmit` fires once HubSpot's own validation has passed and the
		// payload is going out; `onFormSubmitted` fires on the thank-you. Only
		// the first is safe when the form has a redirect configured in the
		// HubSpot dashboard — /book-a-demo/ redirects to an external thank-you
		// page, so the document unloads and cross-origin navigates before
		// `onFormSubmitted` can land, and the submission went uncounted
		// (measured with a real submission).
		//
		// Neither event fires when validation fails, which is what keeps this
		// from counting the rejected attempts the native submit event reports.
		if (data.eventName === 'onFormSubmit' || data.eventName === 'onFormSubmitted') {
			count(container);
		}
	});
};
